/**
 * Ejected from `experimental/shared/findings/findingMeta.tsx` for NEX-18039: an objective-scoped
 * finding's `ScopeMeta` entry now carries a progress bar and health word beside the objective's name,
 * not just the name. That needs `useObjectiveHealthByIdsQuery` data and this variant's own
 * `HEALTH_TONE`/`HEALTH_PROGRESS_CLASS` tokens threaded in — neither of which the shared contract
 * (confidence/group tone as props, no data fetching) supports — so this copy takes two extra
 * parameters the shared version does not have. Every other variant still imports the shared file and
 * is unaffected.
 *
 * Kept as one enriched `ScopeMeta` entry rather than a second element elsewhere on the row: a first
 * pass gave the objective its own line below the meta line, which meant a loop-scoped finding stated
 * its scope inline (`HIGH CONFIDENCE · WHOLE LOOP`) while an objective-scoped one promoted the
 * objective to a separate row shape — same `scope` field, two different treatments. Folding it back
 * into the scope slot it already occupied makes every scope level read the same way: one inline entry,
 * sometimes carrying more than a label.
 */
import { Progress } from '@tempo-io/sui-progress';
import { Skeleton } from '@tempo-io/sui-skeleton';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import { useFindingRefsLookup } from '@/features/agentic-insights/components/data/FindingRefsContext';
import type { Finding } from '@/features/agentic-insights/components/data/types';
import type { ConfidenceToneTokens } from '@/features/agentic-insights/components/experimental/shared/tokens';
import { Field } from '@/features/agentic-insights/components/experimental/shared/ui/Field';
import { ObjectiveLink } from '@/features/agentic-insights/components/experimental/shared/ui/ObjectiveLink';
import { Tooltip } from '@/features/agentic-insights/components/experimental/shared/ui/Tooltip';
import { HEALTH_PROGRESS_CLASS, HEALTH_TONE, TYPE } from '../language';

/**
 * Everything that qualifies a finding rather than stating it: how far it is trusted, the slice of the
 * portfolio it was measured at, and the line that composes them.
 *
 * One file because the parts and the line that arranges them changed together every time — a part
 * defined next door from its only caller is two files to open to answer one question.
 */

/** Confidence, with the reason it reads that way on the level it explains. */
const ConfidenceMeta = ({
  finding,
  confidenceTone,
}: {
  finding: Finding;
  confidenceTone: ConfidenceToneTokens;
}) => {
  const { t } = useTranslation();

  const level = t(($) => $.loopAgenticInsights.confidence[finding.confidence]);
  const basis = finding.confidenceBasis
    ? t(
        ($) =>
          $.loopAgenticInsights.confidenceBasis[
            finding.confidenceBasis ?? 'noUnresolvedAlternative'
          ],
      )
    : undefined;

  return (
    <span className={confidenceTone[finding.confidence]}>
      {basis ? (
        <Tooltip label={<Field name="confidenceBasis">{basis}</Field>}>
          <Field name="confidence">{level}</Field>
        </Tooltip>
      ) : (
        <Field name="confidence">{level}</Field>
      )}
    </span>
  );
};

/**
 * The objective an objective-scoped finding was measured against, given a state rather than only a
 * name — NEX-18039. `scope.ref` is the objective this entry names; a finding can also name others in
 * `objectiveIds` in passing (the contract carries no precedence among them), which show only as a
 * count, since this is one entry on a mono meta line and not a place for a second objective's own bar.
 *
 * Deliberately silent on whether the bar agrees with the claim above it — an objective at 71% HEALTHY
 * under a claim arguing the progress is inflated by cancelled work still shows 71% HEALTHY. This states
 * the system's own number and lets the claim argue with it, rather than hiding or adjusting the bar to
 * match.
 */
const ObjectiveScopeMeta = ({
  finding,
  objectiveHealthById,
  isObjectiveHealthPending,
}: {
  finding: Finding;
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
}) => {
  const { t } = useTranslation();
  const refs = useFindingRefsLookup();

  // `ref` is optional in the schema even when `level === 'objective'` — a stored run from before the
  // field existed, in principle. Nothing to feature without it.
  const ref = finding.scope?.level === 'objective' ? finding.scope.ref : undefined;
  if (ref === undefined) return null;

  if (isObjectiveHealthPending) {
    return <Skeleton data-testid="agentic-x-objective-scope-skeleton" className="h-3 w-32" />;
  }

  const id = Number(ref);
  const health = objectiveHealthById?.[id];
  const title = refs.objective(id);
  const healthLevel = health?.healthLevel ?? undefined;
  const progress = Math.round(health?.progressPercentage ?? 0);
  const moreCount = finding.objectiveIds.filter((other) => other !== ref).length;

  return (
    <span className="flex items-center gap-1.5">
      <Field name="scope">
        {title !== undefined ? (
          <ObjectiveLink>{title}</ObjectiveLink>
        ) : (
          // Unresolved (an objective from a loop other than the one this hook's title lookup is
          // scoped to, most commonly) — named by id rather than dropped, the same "Objective 42"
          // shape `ScopeMeta`'s own initiative/team fallback below uses, so a reader can still tell
          // which objective is meant.
          t(($) => $.loopAgenticInsights.experimental.meta.scope, {
            level: t(($) => $.loopAgenticInsights.experimental.scopeLevel.objective),
            ref,
          })
        )}
      </Field>
      <Progress
        value={progress}
        max={100}
        className="h-1 w-10 shrink-0 bg-bg-neutral-subtle"
        indicatorClassName={healthLevel ? HEALTH_PROGRESS_CLASS[healthLevel] : ''}
      />
      <span className={`${TYPE.metaValue} shrink-0`}>{progress}%</span>
      <span className={`shrink-0 ${healthLevel ? HEALTH_TONE[healthLevel] : ''}`}>
        {healthLevel
          ? t(($) => $.insights.ragStatus[healthLevel])
          : t(($) => $.objectives.healthStatus.unavailable)}
      </span>
      {moreCount > 0 ? (
        <span className="shrink-0">
          {t(($) => $.loopAgenticInsights.experimental.objectiveScope.moreObjectives, {
            count: moreCount,
          })}
        </span>
      ) : null}
    </span>
  );
};

/**
 * The slice of the portfolio the finding was measured at. Loop and objective are both stated inline —
 * a loop-scoped finding says `WHOLE LOOP`, an objective-scoped one says the objective's name plus its
 * bar and health (`ObjectiveScopeMeta`). Initiative and team keep their raw ref: nothing in this
 * feature has a title source for them, and a label that cannot resolve should not look clickable.
 */
const ScopeMeta = ({
  finding,
  objectiveHealthById,
  isObjectiveHealthPending,
}: {
  finding: Finding;
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
}) => {
  const { t } = useTranslation();

  if (!finding.scope) return null;

  if (finding.scope.level === 'loop') {
    return <Field name="scope">{t(($) => $.loopAgenticInsights.finding.scope.loop)}</Field>;
  }

  if (finding.scope.level === 'objective') {
    return (
      <ObjectiveScopeMeta
        finding={finding}
        objectiveHealthById={objectiveHealthById}
        isObjectiveHealthPending={isObjectiveHealthPending}
      />
    );
  }

  const ref = finding.scope.ref;
  const label = t(($) => $.loopAgenticInsights.experimental.meta.scope, {
    level: t(($) => $.loopAgenticInsights.experimental.scopeLevel[finding.scope?.level ?? 'loop']),
    ref: ref ?? '',
  });

  return <Field name="scope">{label}</Field>;
};

/**
 * The mono meta line under a claim.
 *
 * Only what qualifies the claim: how far it is trusted, whether it asks for anything, and the slice
 * of the portfolio it was measured at. Confidence and scope come from `metaParts`, shared with the
 * work order so the same two facts cannot end up rendered two ways.
 *
 * The figure itself is not here. `metric`, `metricQualifier`, `metricLabel` and `denominator` are
 * four fields that ran together into one uppercase line — "1 OF 1,363 THE ONE CONNECTED ITEM IS A
 * TEST PLACEHOLDER ITEMS CONNECTED TO THE OBJECTIVE" — where no reader could tell where the number
 * ended. They are laid out properly in the expansion instead.
 *
 * The finding's group is deliberately absent too: the section it sits under already states it.
 */
export const useFindingMeta = (
  finding: Finding,
  confidenceTone: ConfidenceToneTokens,
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined,
  isObjectiveHealthPending: boolean,
): ReactNode[] => {
  const { t } = useTranslation();

  return [
    <ConfidenceMeta key="confidence" finding={finding} confidenceTone={confidenceTone} />,
    finding.kind === 'report' ? (
      <span key="kind">
        <Field name="kind">{t(($) => $.loopAgenticInsights.finding.reportKind)}</Field>
      </span>
    ) : null,
    finding.scope ? (
      <ScopeMeta
        key="scope"
        finding={finding}
        objectiveHealthById={objectiveHealthById}
        isObjectiveHealthPending={isObjectiveHealthPending}
      />
    ) : null,
  ].filter(Boolean);
};
